import { showToast } from '@veem/ui'
import { ApiError, api, reportRefreshFailure } from '../../lib/api.ts'
import { appendPickedFile } from '../../lib/file-reference.ts'
import { copySnapshot } from '../../lib/snapshot-value.ts'
import { hydrateData } from '../../prototype/data/hydration.ts'
import type { ProtoFile } from '../../prototype/data/orders.ts'
import type { ProtoMaterial, WarehouseName } from '../../prototype/data/warehouse.ts'
import { type ProtoState, proto } from '../../prototype/state/store.ts'
import { purchasingActions } from '../purchasing/actions.ts'
import { supplierActions } from '../suppliers/actions.ts'

export const recordVersion = (record: unknown): number => {
  if (
    record &&
    typeof record === 'object' &&
    'version' in record &&
    typeof record.version === 'number'
  )
    return record.version
  throw new Error('Оновіть сторінку: версія запису недоступна')
}

export const uploadWarehouseFiles = async (files: File[] = []): Promise<ProtoFile[]> => {
  if (!files.length) return []
  const body = new FormData()
  for (const file of files) await appendPickedFile(body, 'files', file)
  let response: Response
  try {
    response = await fetch('/api/warehouse/files', { method: 'POST', body, credentials: 'include' })
  } catch {
    throw new ApiError('Немає зв’язку із сервером. Спробуйте ще раз.', 0, 'NETWORK_ERROR')
  }
  let result: { ok: boolean; data?: ProtoFile[]; error?: { code: string; message: string } }
  try {
    result = await response.json()
  } catch {
    throw new ApiError(
      'Сервер тимчасово недоступний. Спробуйте ще раз.',
      response.status,
      'INVALID_RESPONSE',
    )
  }
  if (!response.ok || !result.ok || !result.data)
    throw new ApiError(
      result.error?.message ?? 'Не вдалося завантажити файли',
      response.status,
      result.error?.code ?? 'UPLOAD_FAILED',
    )
  return result.data
}

const refreshWarehouse = async () => {
  const incoming = copySnapshot(
    await api<{ data: Record<string, unknown>; state: Partial<ProtoState> }>('/snapshot'),
  )
  hydrateData(incoming.data)
  proto.hydrate(incoming.state)
}

export const writeWarehouse = async <T>(
  path: string,
  input: unknown,
  method = 'POST',
): Promise<T> => {
  let result: T
  try {
    result = await api<T>(path, { method, body: JSON.stringify(input) })
  } catch (error) {
    if (error instanceof ApiError && error.status === 409) {
      try {
        await refreshWarehouse()
      } catch {
        /* The original conflict remains actionable. */
      }
    }
    throw error
  }
  try {
    await refreshWarehouse()
  } catch {
    reportRefreshFailure()
  }
  return result
}

export const actionError = (error: unknown): string =>
  error instanceof Error ? error.message : 'Не вдалося зберегти зміни'
export let lastWarehouseError: string | null = null
export const toastAction = async <T>(operation: () => Promise<T>): Promise<T | null> => {
  lastWarehouseError = null
  try {
    return await operation()
  } catch (error) {
    lastWarehouseError = actionError(error)
    showToast(lastWarehouseError, 'error')
    return null
  }
}
const errorAction = async (operation: () => Promise<unknown>): Promise<string | null> => {
  try {
    await operation()
    return null
  } catch (error) {
    return actionError(error)
  }
}

type CreateMaterialInput = Pick<
  ProtoMaterial,
  | 'name'
  | 'grade'
  | 'category'
  | 'unit'
  | 'conversions'
  | 'warehouse'
  | 'location'
  | 'critical'
  | 'supplier'
>
type MaterialPatch = Pick<
  ProtoMaterial,
  | 'name'
  | 'grade'
  | 'category'
  | 'critical'
  | 'unitCost'
  | 'supplier'
  | 'warehouse'
  | 'location'
  | 'conversions'
>
type Place = { materialId: string; warehouse: WarehouseName; location: string }
type Entered = { quantity: string; unit: string }
type StockInput = Place &
  (
    | {
        kind: 'receipt'
        quantity: string
        entered: Entered
        unitCost: string
        ref: string
        buyerEntityId?: string | null
        files?: File[]
        attachments?: ProtoFile[]
      }
    | {
        kind: 'issue' | 'writeoff'
        quantity: string
        entered: Entered
        ref: string | null
        reason: string | null
      }
    | {
        kind: 'transfer'
        quantity: string
        entered: Entered
        to: { warehouse: WarehouseName; location: string }
        reason: string | null
      }
    | { kind: 'adjustment'; counted: string; reason: string }
  )

export const warehouseActions = {
  ...purchasingActions,
  ...supplierActions,
  createMaterial: (input: CreateMaterialInput) =>
    toastAction(
      async () => (await writeWarehouse<{ id: string }>('/warehouse/materials', input)).id,
    ),
  updateMaterial: (id: string, patch: MaterialPatch, version?: number) =>
    errorAction(() =>
      writeWarehouse(
        `/warehouse/materials/${encodeURIComponent(id)}`,
        {
          ...patch,
          version: version ?? recordVersion(proto.state.materials.find((row) => row.id === id)),
        },
        'PATCH',
      ),
    ),
  setCriticalLevel: (id: string, critical: string, version?: number) =>
    toastAction(async () => {
      await writeWarehouse(
        `/warehouse/materials/${encodeURIComponent(id)}/critical`,
        {
          critical,
          version: version ?? recordVersion(proto.state.materials.find((row) => row.id === id)),
        },
        'PATCH',
      )
      return true
    }),
  renameWarehouseItem: (
    kind: 'location' | 'category',
    from: string,
    to: string,
    warehouse?: WarehouseName,
  ) =>
    toastAction(async () => {
      await writeWarehouse('/warehouse/rename', { kind, from, to, warehouse })
      return true
    }),
  stockOperation: (input: StockInput, submittedVersion?: number) =>
    errorAction(async () => {
      const version =
        submittedVersion ??
        recordVersion(proto.state.materials.find((row) => row.id === input.materialId))
      if (input.kind === 'receipt') {
        const { files, attachments = [], ...data } = input
        const uploaded = await uploadWarehouseFiles(files)
        return await writeWarehouse('/warehouse/operations', {
          ...data,
          attachments: [...attachments, ...uploaded],
          version,
        })
      }
      return await writeWarehouse('/warehouse/operations', { ...input, version })
    }),
  confirmIssue: (dealId: string) =>
    errorAction(() =>
      writeWarehouse(`/warehouse/issues/${encodeURIComponent(dealId)}`, {
        version: recordVersion(
          proto.state.orders.find((row) => row.deal.id === dealId)?.production,
        ),
      }),
    ),
  receiveExpected: async (
    id: string,
    input: {
      quantity: string
      unit?: string
      warehouse: WarehouseName
      location: string
      document: string
    },
    version?: number,
  ): Promise<{ toppedUp: number; error?: string }> => {
    try {
      return await writeWarehouse<{ toppedUp: number }>(
        `/warehouse/receipts/${encodeURIComponent(id)}`,
        {
          ...input,
          version: version ?? recordVersion(proto.state.expected.find((row) => row.id === id)),
        },
      )
    } catch (error) {
      return { toppedUp: 0, error: actionError(error) }
    }
  },
}
